export interface Share {
  name: string
  percentage: number
}

/** A tag from pasted text, with the share its "NN%" gave it, if any. */
export interface PastedShare {
  name: string
  percentage: number | null
}

const TOTAL = 100

/** Most freight types an application takes. */
export const MAX_FREIGHT_TYPES = 10

function sum(values: number[]): number {
  return values.reduce((total, value) => total + value, 0)
}

function percentagesOf(shares: Share[]): number[] {
  return shares.map((share) => share.percentage)
}

function copyOf(shares: Share[]): Share[] {
  return shares.map((share) => ({ ...share }))
}

function withPercentages(shares: Share[], parts: number[]): Share[] {
  return shares.map((share, index) => ({ name: share.name, percentage: parts[index] }))
}

function wholePercent(value: number): number {
  return Number.isFinite(value) ? Math.min(TOTAL, Math.max(0, Math.round(value))) : 0
}

function sameName(a: string, b: string): boolean {
  return a.trim().toLowerCase() === b.trim().toLowerCase()
}

/** `total` split in proportion to `weights`, each part floored; evenly when every weight is 0. */
function floorParts(total: number, weights: number[]): number[] {
  const weightSum = sum(weights)
  if (weightSum > 0) return weights.map((weight) => Math.floor((total * weight) / weightSum))
  return weights.map(() => Math.floor(total / weights.length))
}

/** Like `floorParts`, with the rounding remainder on the last part so the parts add up to `total`. */
function distribute(total: number, weights: number[]): number[] {
  if (weights.length === 0) return []
  const parts = floorParts(total, weights)
  parts[parts.length - 1] += total - sum(parts)
  return parts
}

function range(from: number, to: number): number[] {
  return Array.from({ length: Math.max(0, to - from) }, (_, offset) => from + offset)
}

/** Trims and merges names, then makes whole shares totalling 100 (evenly when they total 0). */
export function normalizeShares(shares: Share[]): Share[] {
  const merged: Share[] = []
  for (const share of shares) {
    const name = share.name.trim()
    if (!name) continue
    const percentage = Number.isFinite(share.percentage) ? Math.max(0, share.percentage) : 0
    const existing = merged.find((item) => sameName(item.name, name))
    if (existing) existing.percentage += percentage
    else merged.push({ name, percentage })
  }
  const values = percentagesOf(merged)
  if (values.every((value) => Number.isInteger(value)) && sum(values) === TOTAL) return merged
  return withPercentages(merged, distribute(TOTAL, values))
}

/** Moves one share from the drag-start `snapshot`; the others give or take the difference equally. */
export function moveShare(snapshot: Share[], index: number, value: number): Share[] {
  const next = copyOf(snapshot)
  if (index < 0 || index >= next.length) return next
  if (next.length === 1) return [{ name: next[0].name, percentage: TOTAL }]
  const target = wholePercent(value)
  const others = range(0, next.length).filter((position) => position !== index)
  const parts = spreadEvenly(
    others.map((position) => snapshot[position].percentage),
    target - snapshot[index].percentage
  )
  next[index].percentage = target
  others.forEach((position, offset) => {
    next[position].percentage = parts[offset]
  })
  return next
}

/** Takes `delta` from the parts equally (or gives it to the ones above 0, when negative); a part that runs out leaves the rest to the others. */
function spreadEvenly(parts: number[], delta: number): number[] {
  const next = [...parts]
  if (delta < 0) {
    const given = -delta
    const nonZero = range(0, next.length).filter((position) => next[position] > 0)
    const receivers = nonZero.length > 0 ? nonZero : range(0, next.length)
    const base = Math.floor(given / receivers.length)
    const extraFrom = receivers.length - (given - base * receivers.length)
    receivers.forEach((position, offset) => {
      next[position] += base + (offset >= extraFrom ? 1 : 0)
    })
    return next
  }
  let left = delta
  while (left > 0) {
    const open = range(0, next.length).filter((position) => next[position] > 0)
    if (open.length === 0) break
    const each = Math.floor(left / open.length)
    if (each === 0) {
      for (const position of open.slice(open.length - left)) next[position] -= 1
      break
    }
    for (const position of open) {
      const taken = Math.min(each, next[position])
      next[position] -= taken
      left -= taken
    }
  }
  return next
}

/** Appends a tag with an equal floored share; the others shrink in proportion and the new last tag takes the remainder. */
export function addShare(shares: Share[], name: string): Share[] {
  const trimmed = name.trim()
  const current = copyOf(shares)
  if (!trimmed || current.some((share) => sameName(share.name, trimmed))) return current
  const added = Math.floor(TOTAL / (current.length + 1))
  const parts = floorParts(TOTAL - added, percentagesOf(current))
  return [...withPercentages(current, parts), { name: trimmed, percentage: TOTAL - sum(parts) }]
}

/** Removes a tag and rescales the rest in proportion, or evenly when they are all at 0. */
export function removeShare(shares: Share[], index: number): Share[] {
  if (index < 0 || index >= shares.length) return copyOf(shares)
  const rest = shares.filter((_, position) => position !== index)
  return withPercentages(rest, distribute(TOTAL, percentagesOf(rest)))
}

/** Whole shares as equal as they can be, the remainder on the last ones like everywhere else: 33, 33, 34. */
export function evenShares(shares: Share[]): Share[] {
  if (shares.length === 0) return shares
  const base = Math.floor(TOTAL / shares.length)
  const plusOneFrom = shares.length - (TOTAL - base * shares.length)
  return shares.map((share, index) => ({ ...share, percentage: base + (index >= plusOneFrom ? 1 : 0) }))
}

/** Tags at 0% aren't saved. */
export function dropZeroShares(shares: Share[]): Share[] {
  return copyOf(shares.filter((share) => share.percentage > 0))
}

/** Adds pasted tags; ones that came with a percentage keep it and the rest share what is left in proportion. */
export function pasteShares(shares: Share[], entries: PastedShare[]): Share[] {
  const next = entries.reduce((list, entry) => addShare(list, entry.name), copyOf(shares))
  const pins = new Map<string, number>()
  for (const entry of entries) {
    if (entry.percentage !== null) pins.set(entry.name.trim().toLowerCase(), wholePercent(entry.percentage))
  }
  if (pins.size === 0) return next
  const pinOf = (share: Share) => pins.get(share.name.toLowerCase())
  const pinned = next.filter((share) => pinOf(share) !== undefined)
  const free = next.filter((share) => pinOf(share) === undefined)
  const pinnedValues = pinned.map((share) => pinOf(share) ?? 0)
  const pinnedTotal = sum(pinnedValues)
  // With nothing free to absorb the difference (or pins over 100), the pins themselves rescale.
  const fits = free.length > 0 && pinnedTotal <= TOTAL
  const pinnedParts = fits ? pinnedValues : distribute(TOTAL, pinnedValues)
  const freeParts = fits ? distribute(TOTAL - pinnedTotal, percentagesOf(free)) : free.map(() => 0)
  const byName = new Map(
    [...withPercentages(pinned, pinnedParts), ...withPercentages(free, freeParts)].map((share) => [
      share.name,
      share.percentage,
    ])
  )
  return next.map((share) => ({ name: share.name, percentage: byName.get(share.name) ?? 0 }))
}
